declare global {
    interface Window {
        gm_authFailure: () => void;
    }
}
declare const google: any;
export const passwordRequirements =
    'Das Passwort muss mindestens aus 8 Zeichen bestehen, Groß- und Kleinschreibung und mindestens eine Zahl beinhalten.';
// sorry for the regex https://ihateregex.io/expr/password
export const isValidPassword = (password: string) => /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/.test(password);

let googleMapsAuthFailed = false;
window.gm_authFailure = () => {
    googleMapsAuthFailed = true;
};

function checkAddress(
    street: string,
    postCode: string,
    city: string,
    country: string,
    onValid: () => void,
    onInvalid: () => void,
    onError: () => void,
): void {
    const address = `${street}, ${postCode} ${city}`;
    if (googleMapsAuthFailed || typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
        onError();
        return;
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address, region: country }, (results: any[], status: string) => {
        if (status === 'OK') {
            if (results.length < 1) {
                onInvalid();
                return;
            }
            const result = results[0];
            if (result.types.indexOf('street_address') < 0) {
                onInvalid();
                return;
            }
            let foundPostCode = false;
            let foundCountry = false;
            let foundStreetNumber = false;
            // Do not check for street or city here. For example, the address "Schloss Glanegg 2, 5082 Grödig, AT"
            // does not return "Grödig" in any of the components and returns "Schloß" instead of "Schloss".
            for (const component of result.address_components) {
                if (component.long_name.toLowerCase() === postCode.toLowerCase()) {
                    foundPostCode = true;
                } else if (component.short_name.toLowerCase() === country.toLowerCase()) {
                    foundCountry = true;
                } else if (component.types.indexOf('street_number') >= 0) {
                    foundStreetNumber = true;
                }
            }
            if (foundPostCode && foundCountry && foundStreetNumber) {
                onValid();
            } else {
                onInvalid();
            }
        } else if (status === 'ZERO_RESULTS') {
            onInvalid();
        } else {
            onError();
        }
    });
}

function validatePasswordStrength(form: HTMLFormElement, passwordInput: HTMLInputElement) {
    passwordInput.addEventListener('keyup', setValidity);
    passwordInput.addEventListener('change', () => {
        setValidity();
        passwordInput.reportValidity();
    });

    form.addEventListener(
        'submit',
        (event) => {
            setValidity();
            if (passwordInput.reportValidity() === false) {
                event.preventDefault();
            }
        },
        false,
    );

    function setValidity() {
        if (!isValidPassword(passwordInput.value)) {
            passwordInput.setCustomValidity(passwordRequirements);
        } else {
            passwordInput.setCustomValidity('');
        }
    }
}

function validateRepeatedInput(
    form: HTMLFormElement,
    originalInput: HTMLInputElement,
    repeatedInput: HTMLInputElement,
) {
    originalInput.addEventListener('keyup', setValidity);
    repeatedInput.addEventListener('change', () => {
        setValidity();
        repeatedInput.reportValidity();
    });

    form.addEventListener(
        'submit',
        (event) => {
            setValidity();
            if (repeatedInput.reportValidity() === false) {
                event.preventDefault();
            }
        },
        false,
    );

    function setValidity() {
        if (originalInput.value !== repeatedInput.value) {
            repeatedInput.setCustomValidity('Die Wiederholung stimmt nicht überein.');
        } else {
            repeatedInput.setCustomValidity('');
        }
    }
}

function validateBirthDate(form: HTMLFormElement, birthDateInput: HTMLInputElement) {
    birthDateInput.addEventListener('keyup', setValidity);
    birthDateInput.addEventListener('change', () => {
        setValidity();
        birthDateInput.reportValidity();
    });

    form.addEventListener(
        'submit',
        (event) => {
            setValidity();
            if (birthDateInput.reportValidity() === false) {
                event.preventDefault();
            }
        },
        false,
    );

    function setValidity() {
        if (!birthDateInput.value) {
            birthDateInput.setCustomValidity('');
            return;
        }

        if (/^(0?[1-9]|[12][0-9]|3[01])[.](0?[1-9]|1[012])[.]\d{4}$/.test(birthDateInput.value) === false) {
            birthDateInput.setCustomValidity('Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein.');
        } else {
            birthDateInput.setCustomValidity('');
        }
    }
}

class RegistrationFormValidation {
    private form = document.querySelector('#customer-registration-form') as HTMLFormElement;
    private errorsElement = document.querySelector('#form-errors') as HTMLUListElement;
    private errors = new Map<string, HTMLElement>();
    private blurred = new Set<HTMLElement>();
    private needsAddresscheck = true;

    constructor() {
        if (this.form === null) {
            return;
        }

        const fieldEmail = document.querySelector('#form-email');
        const fieldEmailConfirm = document.querySelector('#form-email-confirmation');
        if (fieldEmail instanceof HTMLInputElement && fieldEmailConfirm instanceof HTMLInputElement) {
            validateRepeatedInput(this.form, fieldEmail, fieldEmailConfirm);
        }

        const fieldPassword = document.querySelector('#form-password');
        const fieldPasswordConfirm = document.querySelector('#form-password-confirmation');
        if (fieldPassword instanceof HTMLInputElement && fieldPasswordConfirm instanceof HTMLInputElement) {
            validateRepeatedInput(this.form, fieldPassword, fieldPasswordConfirm);
        }
        if (fieldPassword instanceof HTMLInputElement) {
            validatePasswordStrength(this.form, fieldPassword);
        }

        const fieldDateOfBirth = document.querySelector('#form-date-of-birth');
        if (fieldDateOfBirth instanceof HTMLInputElement) {
            validateBirthDate(this.form, fieldDateOfBirth);
        }

        const fieldStreet = document.querySelector('#form-street');
        if (fieldStreet instanceof HTMLInputElement) {
            this.validateStreetNumber(fieldStreet);
        }
        this.validateAddress();

        const passwordRequirementsContainer = document.getElementById('password-requirements');
        if (passwordRequirementsContainer) {
            passwordRequirementsContainer.innerText = passwordRequirements;
        }
    }

    private addError(errorMessage: string): void {
        if (!this.errors.has(errorMessage)) {
            const errorElement = document.createElement('li');
            errorElement.innerText = errorMessage;
            this.errors.set(errorMessage, errorElement);
            this.errorsElement.appendChild(errorElement);
            this.updateButton();
        }
    }

    private removeError(errorrMessage: string): void {
        const errorElement = this.errors.get(errorrMessage);
        if (errorElement) {
            this.errors.delete(errorrMessage);
            this.errorsElement.removeChild(errorElement);
            this.updateButton();
        }
    }

    private updateButton() {
        const buttonText = this.form.querySelector('button[type=submit] span');
        if (buttonText instanceof HTMLElement) {
            if (this.errors.size > 0) {
                buttonText.innerText = 'Die Adresse ist korrekt. Jetzt registrieren.';
            } else {
                buttonText.innerText = 'Registrieren';
            }
        }
    }

    private disableButton() {
        const button = this.form.querySelector('button[type=submit]');
        if (button instanceof HTMLButtonElement) {
            button.setAttribute('disabled', 'disabled');
        }
    }

    private enableButton() {
        const button = this.form.querySelector('button[type=submit]');
        if (button instanceof HTMLButtonElement) {
            button.removeAttribute('disabled');
        }
    }

    private validateAddress() {
        const errorMessage =
            'Ihre Adresse konnte nicht überprüft werden. Sind Sie sicher, dass alle Felder korrekt ausgefüllt wurden?';
        const streetInput = document.querySelector('#form-street') as HTMLInputElement;
        const postcodeInput = document.querySelector('#form-postcode') as HTMLInputElement;
        const cityInput = document.querySelector('#form-city') as HTMLInputElement;
        const countryInput = document.querySelector('#form-country') as HTMLInputElement;

        const onInput = () => {
            this.needsAddresscheck = true;
            this.removeError(errorMessage);
        };
        streetInput.addEventListener('input', onInput);
        postcodeInput.addEventListener('input', onInput);
        cityInput.addEventListener('input', onInput);
        countryInput.addEventListener('input', onInput);

        this.form.addEventListener(
            'submit',
            (event) => {
                if (this.needsAddresscheck) {
                    event.preventDefault();
                    this.disableButton();
                    checkAddress(
                        streetInput.value,
                        postcodeInput.value,
                        cityInput.value,
                        countryInput.value,
                        () => {
                            this.needsAddresscheck = false;
                            this.removeError(errorMessage);
                            this.form.submit();
                        },
                        () => {
                            this.enableButton();
                            this.needsAddresscheck = false;
                            this.addError(errorMessage);
                        },
                        () => {
                            this.enableButton();
                            this.needsAddresscheck = false;
                            this.addError(errorMessage);
                        },
                    );
                }
            },
            false,
        );
    }

    private validateStreetNumber(streetInput: HTMLInputElement) {
        const errorMessage =
            'Das Feld "Straße und Hausnummer" enthält keine Zahl. Bitte vergewissern Sie sich, dass eine Hausnummer eingegeben wurde.';
        const checkValidity = () => {
            if (!this.blurred.has(streetInput)) {
                return true;
            }
            if (streetInput.value.match(/[0-9]+/) === null) {
                streetInput.parentElement?.classList.add('form-has-error');
                this.addError(errorMessage);
                return false;
            } else {
                streetInput.parentElement?.classList.remove('form-has-error');
                this.removeError(errorMessage);
                return true;
            }
        };

        streetInput.addEventListener('blur', () => {
            this.blurred.add(streetInput);
            checkValidity();
        });
        streetInput.addEventListener('input', () => checkValidity());
        this.form.addEventListener(
            'submit',
            (event) => {
                // If the error message is already displayed, let the user submit.
                if (!this.errors.has(errorMessage) && !checkValidity()) {
                    event.preventDefault();
                }
            },
            false,
        );
    }
}

// tslint:disable-next-line no-unused-expression
new RegistrationFormValidation();

const confirmWithPasswordForm = document.querySelector('.confirm-with-pw-form');
if (confirmWithPasswordForm instanceof HTMLFormElement) {
    const fieldPassword = confirmWithPasswordForm.querySelector('#form-password');
    const fieldPasswordConfirm = confirmWithPasswordForm.querySelector('#form-password-confirmation');
    if (fieldPassword instanceof HTMLInputElement && fieldPasswordConfirm instanceof HTMLInputElement) {
        validateRepeatedInput(confirmWithPasswordForm, fieldPassword, fieldPasswordConfirm);
    }
    if (fieldPassword instanceof HTMLInputElement) {
        validatePasswordStrength(confirmWithPasswordForm, fieldPassword);
    }
}
